import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { pathFor, type Lang } from '../../../router/paths';

export default function Contact() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang: Lang = i18n.language === 'en' ? 'en' : 'de';

  useEffect(() => {
    const interval = setInterval(() => {
      if ((window as any).dirs21) {
        try {
          (window as any).dirs21.render();
          (window as any).dirs21.rebindTriggers();
        } catch (e) {}
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="kontakt" className="py-12 sm:py-20 px-4 sm:px-6 bg-stone-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-gray-800 mb-4">
            {t('contactTitle')}
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            {t('contactLead')}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg mb-8 sm:mb-12">
          <div className="bg-amber-700 px-6 sm:px-8 py-4 sm:py-5 flex items-center gap-3 rounded-t-xl">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-calendar-check-line text-white text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-serif text-white font-semibold">{t('contactBookTitle')}</h3>
              <p className="text-amber-200 text-xs">{t('contactBookSub')}</p>
            </div>
          </div>
          <div className="p-6 sm:p-8">
            <div id="dirs21-widget">
              <div className="d21-quickbook"></div>
            </div>
            <p className="mt-6 text-sm text-gray-500 text-center max-w-2xl mx-auto">
              {t('contactNoRoom')}{' '}
              <a href="mailto:hotel@alpenhof-gauting.de" className="text-amber-700 hover:underline font-medium">
                {t('contactNoRoomLink')}
              </a>
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-4 items-center">
              <button
                onClick={() => { navigate(pathFor('buchen', lang)); window.scrollTo({ top: 0, behavior: 'instant' }); }}
                className="w-full sm:w-auto bg-amber-700 text-white font-semibold py-3 px-8 rounded-md text-sm tracking-wide hover:bg-amber-600 transition-colors flex items-center justify-center gap-2 cursor-pointer"
              >
                <i className="ri-arrow-right-line text-white text-sm"></i>
                {t('contactBookCta')}
              </button>
              <p className="text-xs text-gray-400">
                {t('contactBookPhone')} <span className="text-amber-700 font-medium">+49 (0) 89 85 64 29 10</span>
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
          <div>
            <div className="bg-white p-6 sm:p-8 rounded-lg shadow-lg">
              <h3 className="text-xl sm:text-2xl font-serif text-gray-800 mb-5 sm:mb-6">{t('contactInfoTitle')}</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                    <i className="ri-map-pin-line text-amber-700 text-sm sm:text-base"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">{t('contactAddress')}</h4>
                    <p className="text-gray-600 text-sm whitespace-pre-line">
                      {t('contactAddressLines')}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                    <i className="ri-phone-line text-amber-700 text-sm sm:text-base"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">{t('contactPhone')}</h4>
                    <a href="tel:+498985642910" className="text-gray-600 text-sm hover:text-amber-700 transition-colors">+49 (0) 89 85 64 29 10</a>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                    <i className="ri-mail-line text-amber-700 text-sm sm:text-base"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">{t('contactEmail')}</h4>
                    <a href="mailto:hotel@alpenhof-gauting.de" className="text-gray-600 text-sm hover:text-amber-700 transition-colors">hotel@alpenhof-gauting.de</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="bg-white p-6 sm:p-8 rounded-lg shadow-lg">
              <h3 className="text-xl sm:text-2xl font-serif text-gray-800 mb-5 sm:mb-6">{t('contactArrivalTitle')}</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                    <i className="ri-car-line text-amber-700"></i>
                    {t('contactCarTitle')}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                    {t('contactCarText')}
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                    <i className="ri-train-line text-amber-700"></i>
                    {t('contactTrainTitle')}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t('contactTrainText')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
